import { useContext, useEffect, useRef, useState } from 'react';
import { FC, ReactNode } from 'react';
import { useLocation, matchPath } from 'react-router-dom';
import { Link as RouterLink } from 'react-router-dom';
import PerfectScrollbar from 'react-perfect-scrollbar';
import PropTypes from 'prop-types';
import {
  Avatar,
  Box,
  Chip,
  Divider,
  Drawer,
  Link,
  List,
  Button,
  Icon,
  Typography,
} from '@mui/material';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';

import {
  CreditCard as CreditCardIcon,
  Cpu as CpuIcon,
  BarChart as BarChartIcon,
  FileText,
  Send as SendIcon,
  Archive as ArchiveIcon,
  CheckSquare as CheckSquareIcon,
  User as UserIcon,
  Zap as zapIcon,
  Layout as ReportsIcon,
  Repeat as loop,
} from 'react-feather';
import useAuth from 'src/hooks/useAuth';
import NavItem from './NavItem';
import UserContext from 'src/contexts/UserContext';
import { getCompanyLogoSignedUrl } from 'src/models/Companies';

interface NavBarProps {
  onMobileClose: () => void;
  openMobile: boolean;
  onDesktopClose: () => void;
  openDesktop: boolean;
}

interface Item {
  href?: string;
  icon?: ReactNode;
  info?: ReactNode;
  items?: Item[];
  title: string;
  country?: string;
  onClick?: () => void;
}

interface Section {
  items: Item[];
  subheader: string;
}

const sections: Section[] = [
  {
    subheader: 'Accounting',
    items: [
      {
        title: 'Cash Flow',
        icon: BarChartIcon,
        href: '/app/reports/dashboard',
      },
      {
        title: 'Financial Reports',
        icon: ReportsIcon,
        href: '/app/financial-statements',
      },
      {
        title: 'Tax Filing',
        href: '/app/management/taxes',
        country: 'CA',
        icon: CheckSquareIcon,
      },
      {
        title: 'Tax Filing',
        href: '/app/management/income-tax-filing-options',
        country: 'US',
        icon: CheckSquareIcon,
      },
    ],
  },
  {
    subheader: 'Bookkeeping',
    items: [
      {
        title: 'AI Bookkeeper',
        icon: zapIcon,
        href: '/app/aibookkeeper',
        info: () => <Chip color="secondary" size="small" label="New" />,
      },
      {
        title: 'Automation Formulas',
        icon: CpuIcon,
        href: '/app/automation/overview',
      },
      {
        title: 'Smart Shoebox',
        href: '/app/management/receipts',
        icon: ArchiveIcon,
        info: () => <Chip color="secondary" size="small" label="New" />,
      },
      {
        title: 'Transactions',
        icon: FileText,
        href: '/app/management/transactions',
      },
    ],
  },
  {
    subheader: 'Sales',
    items: [
      {
        title: 'Invoicing',
        href: '/app/management/invoices',
        icon: SendIcon,
        info: () => <Chip color="secondary" size="small" label="New" />,
      },
    ],
  },
  {
    subheader: 'Profile',
    items: [
      {
        title: 'Bank Accounts',
        href: '/app/account/bank',
        icon: CreditCardIcon,
      },
      {
        title: 'Settings',
        href: '/app/profile',
        icon: UserIcon,
      },
    ],
  },
];

function renderNavItems({
  items,
  pathname,
  depth = 0,
}: {
  items: Item[];
  pathname: string;
  depth?: number;
}) {
  return (
    <List disablePadding>
      {items.reduce(
        (acc, item) => reduceChildRoutes({ acc, item, pathname, depth }),
        []
      )}
    </List>
  );
}

function reduceChildRoutes({
  acc,
  pathname,
  item,
  depth,
}: {
  acc: any[];
  pathname: string;
  item: Item;
  depth: number;
}) {
  const key = item.title + depth;

  if (item.items) {
    const open = matchPath(pathname, {
      path: item.href,
      exact: false,
    });

    acc.push(
      <NavItem
        depth={depth}
        icon={item.icon}
        info={item.info}
        key={key}
        open={Boolean(open)}
        title={item.title}
        onClick={item.onClick}
      >
        {renderNavItems({
          depth: depth + 1,
          pathname,
          items: item.items,
        })}
      </NavItem>
    );
  } else {
    acc.push(
      <NavItem
        depth={depth}
        href={item.href}
        icon={item.icon}
        info={item.info}
        key={key}
        title={item.title}
        onClick={item.onClick}
      />
    );
  }

  return acc;
}

const DRAWER_WIDTH = 240;

const NavBar: FC<NavBarProps> = ({
  onMobileClose,
  openMobile,
  onDesktopClose,
  openDesktop,
}) => {
  const location = useLocation();
  const { user } = useAuth();
  const companyData = useContext(UserContext).userData;
  const [companyLogoUrl, setCompanyLogoUrl] = useState<string | undefined>();
  const refreshTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    if (openMobile && onMobileClose) {
      onMobileClose();
    }
    if (openDesktop && onDesktopClose) {
      onDesktopClose();
    }
  }, [location.pathname]);

  useEffect(() => {
    let isMounted = true;
    const clearRefreshTimeout = () => {
      if (refreshTimeoutRef.current) {
        clearTimeout(refreshTimeoutRef.current);
        refreshTimeoutRef.current = null;
      }
    };

    const scheduleRefresh = (expiresAt?: string) => {
      clearRefreshTimeout();
      if (!expiresAt) return;

      const expiryTime = new Date(expiresAt).getTime();
      const now = Date.now();
      if (Number.isNaN(expiryTime) || expiryTime <= now) return;

      const refreshDelay = Math.max(
        expiryTime - now - 5 * 60 * 1000,
        60 * 1000
      );
      refreshTimeoutRef.current = setTimeout(() => {
        fetchCompanyLogo();
      }, refreshDelay);
    };

    const fetchCompanyLogo = async () => {
      clearRefreshTimeout();
      if (!companyData?.companyID) {
        if (isMounted) setCompanyLogoUrl(undefined);
        return;
      }

      try {
        const response = await getCompanyLogoSignedUrl(companyData.companyID);
        if (!isMounted) return;

        if (response?.signedUrl) {
          setCompanyLogoUrl(response.signedUrl);
          scheduleRefresh(response.expiresAt);
        } else {
          setCompanyLogoUrl(undefined);
        }
      } catch (error) {
        if (isMounted) setCompanyLogoUrl(undefined);
      }
    };

    fetchCompanyLogo();

    return () => {
      isMounted = false;
      clearRefreshTimeout();
    };
  }, [companyData?.companyID, companyData?.companyLogoFileName]);

  const isCanadian = companyData?.companyLocationHQ?.includes('CA-');
  const filterItems = (items: Item[]) =>
    items.filter((item) =>
      isCanadian ? item.country !== 'US' : item.country !== 'CA'
    );

  const mainSections = sections.filter(
    (s) => s.subheader !== 'Profile'
  );
  const profileSection = sections.find(
    (s) => s.subheader === 'Profile'
  );
  const isLucaActive = Boolean(
    matchPath(location.pathname, { path: '/app/luca', exact: false })
  );

  const content = (
    <Box height="100%" display="flex" flexDirection="column">
      {/* Scrollable area */}
      <Box sx={{ flex: 1, minHeight: 0 }}>
        <PerfectScrollbar options={{ suppressScrollX: true }}>
          {/* Company header */}
          <Box sx={{ px: 2, pt: { xs: 1.5, lg: 2 }, pb: { xs: 1, lg: 1.5 } }}>
            <Link
              component={RouterLink}
              to="/app/profile"
              underline="none"
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1.5,
                borderRadius: '10px',
                p: '6px',
                mx: '-6px',
                transition: 'background-color 0.15s ease',
                '&:hover': { backgroundColor: 'rgba(0,0,0,0.04)' },
              }}
            >
              <Avatar
                alt={companyData.companyName}
                src={companyLogoUrl || user.avatar}
                sx={{ width: 36, height: 36, flexShrink: 0 }}
              />
              <Typography
                sx={{
                  color: 'text.primary',
                  fontWeight: 700,
                  fontSize: '0.875rem',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  flex: 1,
                  minWidth: 0,
                }}
              >
                {companyData.companyName}
              </Typography>
            </Link>

            {companyData.userBusinesses?.length > 1 && (
              <Box sx={{ mt: 0.5, pl: '48px' }}>
                <Button
                  variant="text"
                  size="small"
                  component={RouterLink}
                  to="/app/collaboration/choose-business/"
                  startIcon={<Icon component={loop} style={{ fontSize: 11 }} />}
                  sx={{
                    fontSize: '0.75rem',
                    color: 'text.secondary',
                    px: 0.5,
                    py: 0.25,
                    minHeight: 'unset',
                    textTransform: 'none',
                    '&:hover': {
                      color: 'secondary.main',
                      backgroundColor: 'transparent',
                    },
                  }}
                >
                  Switch Business
                </Button>
              </Box>
            )}
          </Box>

          <Divider />

          {/* Luca */}
          <Box sx={{ px: 1.5, pt: { xs: 1, lg: 2 }, pb: 0 }}>
            <Link
              component={RouterLink}
              to="/app/luca"
              underline="none"
              sx={{
                width: '100%',
                display: 'flex',
                alignItems: 'center',
                gap: 1.5,
                px: 1.5,
                py: '9px',
                borderRadius: '8px',
                backgroundColor: isLucaActive ? 'rgba(88,80,236,0.07)' : 'transparent',
                transition: 'filter 0.2s ease, background-color 0.15s ease, transform 0.2s ease',
                '@keyframes lucaGlow': {
                  '0%, 100%': { filter: 'drop-shadow(0 0 3px rgba(88,80,236,0.2))' },
                  '50%': { filter: 'drop-shadow(0 0 8px rgba(139,92,246,0.55))' },
                },
                filter: isLucaActive ? 'drop-shadow(0 0 7px rgba(88,80,236,0.5))' : undefined,
                animation: isLucaActive ? 'none' : 'lucaGlow 3s ease-in-out infinite',
                '&:hover': {
                  backgroundColor: 'rgba(88,80,236,0.07)',
                  filter: 'drop-shadow(0 0 10px rgba(88,80,236,0.6))',
                  transform: 'translateY(-1px)',
                  animation: 'none',
                },
              }}
            >
              <AutoAwesomeIcon sx={{ fontSize: 17, flexShrink: 0, color: 'secondary.main' }} />
              <Box
                component="span"
                sx={{
                  flex: 1,
                  fontSize: '0.9rem',
                  fontWeight: 700,
                  letterSpacing: '-0.01em',
                  background: 'linear-gradient(135deg, #5850EC 0%, #8B5CF6 100%)',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  backgroundClip: 'text',
                }}
              >
                Chat with Luca
              </Box>
            </Link>
          </Box>

          {/* Main nav sections */}
          <Box sx={{ px: 1.5, pt: { xs: 1, lg: 2 }, pb: 1 }}>
            {mainSections.map((section) => {
              const filteredItems = filterItems(section.items);
              if (filteredItems.length === 0) return null;
              return (
                <Box key={section.subheader} sx={{ mb: { xs: 2, lg: 3 } }}>
                  <Typography
                    sx={{
                      display: 'block',
                      px: 1.5,
                      mb: 0.5,
                      fontSize: '0.65rem',
                      fontWeight: 700,
                      letterSpacing: '0.1em',
                      color: 'text.disabled',
                      textTransform: 'uppercase',
                    }}
                  >
                    {section.subheader}
                  </Typography>
                  {renderNavItems({
                    items: filteredItems,
                    pathname: location.pathname,
                  })}
                </Box>
              );
            })}
          </Box>
        </PerfectScrollbar>
      </Box>

      {/* Bottom: profile / settings */}
      {profileSection && (
        <Box>
          <Divider />
          <Box sx={{ px: 1.5, py: { xs: 1, lg: 1.5 } }}>
            {renderNavItems({
              items: filterItems(profileSection.items),
              pathname: location.pathname,
            })}
          </Box>
        </Box>
      )}
    </Box>
  );

  return (
    <>
      <Box sx={{ display: { lg: 'none' } }}>
        <Drawer
          anchor="left"
          sx={{ '& .MuiDrawer-paper': { width: DRAWER_WIDTH } }}
          onClose={onMobileClose}
          open={openMobile}
          variant="temporary"
        >
          {content}
        </Drawer>
      </Box>
      <Box sx={{ display: { xs: 'none', lg: 'block' } }}>
        <Drawer
          anchor="left"
          sx={{
            '& .MuiDrawer-paper': {
              width: DRAWER_WIDTH,
              top: 41,
              height: 'calc(100% - 41px)',
            },
          }}
          onClose={onDesktopClose}
          open={openDesktop}
          variant="persistent"
        >
          {content}
        </Drawer>
      </Box>
    </>
  );
};

NavBar.propTypes = {
  onMobileClose: PropTypes.func,
  openMobile: PropTypes.bool,
  onDesktopClose: PropTypes.func,
  openDesktop: PropTypes.bool,
};

export default NavBar;
